<template>
  <div class="col-12 col-xl-12">
    <div class="container-fluid px-0">

      <!-- Promotion Filters / Setup -->
      <div class="card mb-4 mt-3 mx-2 mx-md-4" style="overflow: visible">
        <div
          class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
          style="height: 60px"
        >
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
            <div class="d-flex justify-content-between align-items-center px-3">
              <h6 class="text-white text-capitalize mb-0">
                <i class="fas fa-layer-group me-2"></i>
                {{ $t('promotion.execute_promotion') }}
              </h6>

              <div class="d-none d-md-flex align-items-center text-white-50 small">
                <i class="fas fa-user-graduate me-1"></i>
                <span>
                  {{ $t('students.students') || 'Students' }}:
                  <strong class="text-white ms-1">{{ students.length }}</strong>
                </span>
                <span class="mx-2">•</span>
                <i class="fas fa-check-circle me-1"></i>
                <span>
                  {{ $t('promotion.selected') || 'Selected' }}:
                  <strong class="text-white ms-1">{{ selectedStudents.length }}</strong>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="card-body pt-3 pb-3 pb-md-4 px-3 px-md-4">
          <div class="row g-3 g-lg-4 align-items-stretch">

            <div class="col-12 col-lg-4">
              <div class="border rounded-3 p-3 h-100 bg-white">
                <div class="d-flex align-items-center mb-3">
                  <span class="badge bg-secondary-subtle text-secondary border rounded-pill ms-2">
                    1
                  </span>
                  <h6 class="mb-0 text-sm fw-bold">
                    {{ $t('promotion.select_source') || $t('promotion.select_class') }}
                  </h6>
                </div>

                <div class="mb-3">
                  <label class="form-label text-xs fw-bold mb-1">
                    {{ $t('promotion.select_class') }}
                  </label>
                  <select
                    v-model="filters.sourceClassId"
                    class="form-select form-select-sm border border-2 border-gray-300 shadow-xs"
                    @change="onClassChange"
                  >
                    <option value="">{{ $t('promotion.select_class') }}</option>
                    <option
                      v-for="classItem in classes"
                      :value="classItem.id"
                      :key="classItem.id"
                    >
                      {{ classItem.name }}
                    </option>
                  </select>
                  <small class="text-muted d-block mt-1 text-xxs">
                    {{ $t('promotion.source_class_help') || '' }}
                  </small>
                </div>

                <div>
                  <label class="form-label text-xs fw-bold mb-1">
                    {{ $t('promotion.select_group') }}
                  </label>
                  <select
                    v-model="filters.sourceClassGroupId"
                    class="form-select form-select-sm border border-2 border-gray-300 shadow-xs"
                    @change="onGroupChange"
                    :disabled="!filters.sourceClassId"
                  >
                    <option value="">{{ $t('promotion.select_group') }}</option>
                    <option
                      v-for="group in sourceGroups"
                      :key="group.id"
                      :value="group.id"
                    >
                      {{ group.name }}
                    </option>
                  </select>
                  <small class="text-muted d-block mt-1 text-xxs">
                    {{ $t('promotion.source_group_help') || '' }}
                  </small>
                </div>
              </div>
            </div>

            <div class="col-12 col-lg-4">
              <div class="border rounded-3 p-3 h-100 bg-white">
                <div class="d-flex align-items-center mb-3">
                  <span class="badge bg-secondary-subtle text-secondary border rounded-pill ms-2">
                    2
                  </span>
                  <h6 class="mb-0 text-sm fw-bold">
                    {{ $t('promotion.target_settings') || $t('promotion.select_year') }}
                  </h6>
                </div>

                <div class="mb-3">
                  <label class="form-label text-xs fw-bold mb-1">
                    {{ $t('promotion.select_year') }}
                  </label>
                  <select
                    v-model="promotionData.targetAcademicYearId"
                    class="form-select form-select-sm border border-2 border-gray-300 shadow-xs"
                  >
                    <option value="">{{ $t('promotion.select_year') }}</option>
                    <option
                      v-for="year in academicYears"
                      :key="year.id"
                      :value="year.id"
                    >
                      {{ year.name }} ({{ year.year }})
                      <span v-if="year.is_active"> - {{ $t('common.current') || '' }}</span>
                    </option>
                  </select>
                </div>

                <div class="mb-3">
                  <label class="form-label text-xs fw-bold mb-1">
                    {{ $t('promotion.select_class') }}
                  </label>
                  <select
                    v-model="promotionData.targetClassId"
                    class="form-select form-select-sm border border-2 border-gray-300 shadow-xs"
                    @change="loadTargetClassGroups"
                  >
                    <option value="">{{ $t('promotion.select_class') }}</option>
                    <option
                      v-for="classItem in classes"
                      :key="classItem.id"
                      :value="classItem.id"
                    >
                      {{ classItem.name }}
                    </option>
                  </select>
                </div>

                <div>
                  <label class="form-label text-xs fw-bold mb-1">
                    {{ $t('promotion.select_group') }}
                  </label>
                  <select
                    v-model="promotionData.targetClassGroupId"
                    class="form-select form-select-sm border border-2 border-gray-300 shadow-xs"
                    :disabled="!promotionData.targetClassId || targetGroups.length === 0"
                  >
                    <option value="">{{ $t('promotion.select_group') }}</option>
                    <option
                      v-for="group in targetGroups"
                      :key="group.id"
                      :value="group.id"
                    >
                      {{ group.name }}
                    </option>
                  </select>
                  <small
                    v-if="promotionData.targetClassId && targetGroups.length === 0"
                    class="text-warning d-block mt-1 text-xxs"
                  >
                    {{ $t('promotion.no_groups_in_class') || '' }}
                  </small>
                </div>
              </div>
            </div>

            <div class="col-12 col-lg-4">
              <div class="border rounded-3 p-3 h-100 bg-white d-flex flex-column justify-content-between">
                <div>
                  <div class="d-flex align-items-center mb-3">
                    <span class="badge bg-secondary-subtle text-secondary border rounded-pill ms-2">
                      3
                    </span>
                    <h6 class="mb-0 text-sm fw-bold">
                      {{ $t('promotion.promotion_type') || $t('promotion.promote') }}
                    </h6>
                  </div>

                  <div class="mb-3">
                    <label class="form-label text-xs fw-bold mb-1">
                      {{ $t('promotion.promotion_type') || $t('promotion.promote') }}
                    </label>
                    <select
                      v-model="promotionData.promotionType"
                      class="form-select form-select-sm border border-2 border-gray-300 shadow-xs"
                    >
                      <option value="promote">{{ $t('promotion.promote') }}</option>
                      <option value="repeat">{{ $t('promotion.repeat') }}</option>
                    </select>
                  </div>

                  <div class="small text-muted mb-3">
                    <div class="d-flex justify-content-between mb-1">
                      <span>{{ $t('promotion.selected') || 'Selected' }}</span>
                      <strong>{{ selectedStudents.length }}</strong>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span>{{ $t('students.students') || 'Students' }}</span>
                      <strong>{{ students.length }}</strong>
                    </div>
                  </div>
                </div>

                <div class="mt-2">
                  <div class="d-flex flex-column flex-md-row gap-2">
                    <button
                      class="btn btn-outline-secondary btn-sm w-100 shadow-xs"
                      @click="resetFilters"
                      :disabled="loadingStudents || promoting"
                    >
                      <i class="fas fa-filter-circle-xmark me-1"></i>
                      {{ $t('common.reset') || '' }}
                    </button>

                    <button
                      class="btn btn-primary btn-sm w-100 shadow-xs"
                      :disabled="!canPromote || promoting"
                      @click="promoteStudents"
                    >
                      <i class="fas fa-graduation-cap me-2"></i>
                      {{ promoting ? $t('common.processing') : $t('promotion.execute_promotion') }}
                    </button>
                  </div>

                  <small class="text-muted d-block mt-2 text-xxs">
                    {{ $t('promotion.hint') || '' }}
                  </small>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="text-white text-capitalize ps-3">{{ $t('promotion.students_list') }}</h6>
              <div class="ms-2">
                <button class="btn btn-sm btn-outline-light me-2" @click="selectAllStudents">
                  {{ $t('promotion.select_all') }}
                </button>
                <button class="btn btn-sm btn-outline-light" @click="deselectAllStudents">
                  {{ $t('promotion.deselect_all') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="card-body px-0 pb-2">
          <div class="table-responsive p-2 p-md-4">
            <LoadingSpinner
              v-if="loadingStudents"
              :message="$t('promotion.loading_students')"
            />

            <template v-else>
              <!-- Desktop table -->
              <div class="d-none d-xl-block">
                <div class="table-responsive">
                  <table class="table align-items-center mb-0" style="width: 100%">
                    <thead>
                      <tr>
                        <th
                          class="text-uppercase text-xxs font-weight-bolder opacity-7"
                          style="width: 5%"
                        >
                          <input
                            type="checkbox"
                            v-model="allSelected"
                            @change="toggleAllSelection"
                          >
                        </th>
                        <th
                          class="text-uppercase text-xxs font-weight-bolder opacity-7"
                          style="width: 10%"
                        >
                          {{ $t('students.id') }}
                        </th>
                        <th
                          class="text-uppercase text-xxs font-weight-bolder opacity-7"
                          style="width: 30%"
                        >
                          {{ $t('students.name') }}
                        </th>
                        <th
                          class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center"
                          style="width: 20%"
                        >
                          {{ $t('students.class') }}
                        </th>
                        <th
                          class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center"
                          style="width: 20%"
                        >
                          {{ $t('students.group') }}
                        </th>
                        <th
                          class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center"
                          style="width: 15%"
                        >
                          {{ $t('students.status') }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="student in students" :key="student.id">
                        <td>
                          <input
                            type="checkbox"
                            v-model="selectedStudents"
                            :value="student.id"
                          >
                        </td>
                        <td>{{ student.id }}</td>
                        <td>
                          {{ student.first_name }} {{ student.father_name }} {{ student.last_name }}
                        </td>
                        <td class="text-center">
                          {{ student.class_groups[0]?.class?.name }}
                        </td>
                        <td class="text-center">
                          {{ student.class_groups[0]?.group?.name }}
                        </td>
                        <td class="text-center">
                          <span class="badge bg-success">{{ $t('students.active') }}</span>
                        </td>
                      </tr>
                      <tr v-if="students.length === 0">
                        <td colspan="6" class="text-center py-4 text-muted">
                          <i class="fas fa-users fa-2x mb-2"></i>
                          <p>{{ $t('promotion.no_students_found') }}</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Mobile cards -->
              <div class="d-xl-none">
                <div
                  v-if="students.length === 0"
                  class="text-center py-4 text-muted"
                >
                  <i class="fas fa-users fa-2x mb-2"></i>
                  <p>{{ $t('promotion.no_students_found') }}</p>
                </div>

                <div v-else class="row g-3">
                  <div
                    class="col-12"
                    v-for="student in students"
                    :key="student.id"
                  >
                    <div class="card shadow-xs border">
                      <div class="card-body">
                        <div class="d-flex justify-content-between align-items-start mb-3">
                          <div class="form-check">
                            <input
                              type="checkbox"
                              v-model="selectedStudents"
                              :value="student.id"
                              class="form-check-input"
                            >
                          </div>
                          <span class="badge bg-success">
                            {{ $t('students.active') }}
                          </span>
                        </div>

                        <div class="d-flex justify-content-between mb-2">
                          <span class="text-secondary">{{ $t('students.id') }}:</span>
                          <strong>{{ student.id }}</strong>
                        </div>

                        <div class="d-flex justify-content-between mb-2">
                          <span class="text-secondary">{{ $t('students.name') }}:</span>
                          <strong>
                            {{ student.first_name }} {{ student.father_name }} {{ student.last_name }}
                          </strong>
                        </div>

                        <div class="d-flex justify-content-between mb-2">
                          <span class="text-secondary">{{ $t('students.class') }}:</span>
                          <span>{{ student.class_groups[0]?.class?.name }}</span>
                        </div>

                        <div class="d-flex justify-content-between">
                          <span class="text-secondary">{{ $t('students.group') }}:</span>
                          <span>{{ student.class_groups[0]?.group?.name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import api from '@/api'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'

const { t, locale } = useI18n()
const toast = useToast()

const academicYears = ref([])
const classes = ref([])
const students = ref([])
const sourceGroups = ref([])
const targetGroups = ref([])
const loadingStudents = ref(false)
const promoting = ref(false)
const selectedStudents = ref([])
const allSelected = ref(false)

const filters = ref({
  sourceClassId: '',
  sourceClassGroupId: ''
})

const promotionData = ref({
  targetAcademicYearId: '',
  targetClassId: '',
  targetClassGroupId: '',
  promotionType: 'promote'
})

const canPromote = computed(() => {
  return (
    selectedStudents.value.length > 0 &&
    promotionData.value.targetAcademicYearId &&
    promotionData.value.targetClassId &&
    promotionData.value.targetClassGroupId
  )
})

function extractApiMessage (error, fallbackKey = 'errors.generic') {
  const data = error?.response?.data
  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en'
      if (typeof data.message[loc] === 'string') return data.message[loc]
    }
    if (typeof data.message === 'string') return data.message
  }
  if (error?.response?.statusText) return error.response.statusText
  return t(fallbackKey)
}

function notifyError (error, fallbackKey = 'errors.generic') {
  const s = error?.response?.status
  if (s === 401) return toast.error(t('auth.unauthorized', 'Unauthorized'))
  if (s === 403) return toast.error(t('auth.forbidden', 'Forbidden'))
  return toast.error(extractApiMessage(error, fallbackKey))
}

const loadInitialData = async () => {
  try {
    const response = await api.get('/student-promotion/dashboard')
    const data = response.data.data

    academicYears.value = data.academic_years || []
    classes.value = data.classes || []

    const currentYear = data.current_academic_year
    if (currentYear) {
      promotionData.value.targetAcademicYearId = currentYear.id
    }
  } catch (error) {
    notifyError(error, 'errors.load_failed')
  }
}

const loadSourceClassGroups = async () => {
  if (!filters.value.sourceClassId) {
    sourceGroups.value = []
    return
  }

  try {
    const response = await api.get(`/classes/${filters.value.sourceClassId}/groups`)
    sourceGroups.value = response.data.data || []
  } catch (error) {
    notifyError(error, 'errors.load_failed')
    sourceGroups.value = []
  }
}

const loadTargetClassGroups = async () => {
  if (!promotionData.value.targetClassId) {
    targetGroups.value = []
    promotionData.value.targetClassGroupId = ''
    return
  }

  try {
    const response = await api.get(`/classes/${promotionData.value.targetClassId}/groups`)
    targetGroups.value = response.data.data || []

    if (targetGroups.value.length === 1) {
      promotionData.value.targetClassGroupId = targetGroups.value[0].id
    } else {
      promotionData.value.targetClassGroupId = ''
    }
  } catch (error) {
    notifyError(error, 'errors.load_failed')
    targetGroups.value = []
    promotionData.value.targetClassGroupId = ''
  }
}

const loadStudents = async () => {
  if (!filters.value.sourceClassId) {
    students.value = []
    selectedStudents.value = []
    allSelected.value = false
    return
  }

  loadingStudents.value = true
  try {
    const params = {
      class_id: filters.value.sourceClassId
    }

    if (filters.value.sourceClassGroupId) {
      params.class_group_id = filters.value.sourceClassGroupId
    }

    const response = await api.get('/student-promotion/students', { params })

    if (response.data && Array.isArray(response.data.data)) {
      students.value = response.data.data
    } else {
      students.value = []
    }

    selectedStudents.value = []
    allSelected.value = false
  } catch (error) {
    notifyError(error, 'errors.load_failed')
    students.value = []
  } finally {
    loadingStudents.value = false
  }
}

const onClassChange = async () => {
  if (!filters.value.sourceClassId) {
    sourceGroups.value = []
    students.value = []
    selectedStudents.value = []
    filters.value.sourceClassGroupId = ''
    return
  }

  filters.value.sourceClassGroupId = ''

  await loadSourceClassGroups()
  await loadStudents()
}

const onGroupChange = async () => {
  await loadStudents()
  selectedStudents.value = []
  allSelected.value = false
}

const selectAllStudents = () => {
  selectedStudents.value = students.value.map(student => student.id)
  allSelected.value = true
}

const deselectAllStudents = () => {
  selectedStudents.value = []
  allSelected.value = false
}

const toggleAllSelection = () => {
  if (allSelected.value) {
    selectAllStudents()
  } else {
    deselectAllStudents()
  }
}
const promoteStudents = async () => {
  if (!canPromote.value || promoting.value) return;

  promoting.value = true;

  try {
    const currentYear = academicYears.value.find(year => year.is_active);
    if (!currentYear) {
      toast.error(t('promotion.no_active_year'));
      return;
    }

    const payload = {
      source_academic_year_id: currentYear.id,
      target_academic_year_id: promotionData.value.targetAcademicYearId,
      student_ids: selectedStudents.value,
      target_class_id: promotionData.value.targetClassId,
      target_class_group_id: promotionData.value.targetClassGroupId,
      promotion_type: promotionData.value.promotionType
    };

    const response = await api.post('/student-promotion/promote', payload);

    const results = response.data.data;
    const successfulCount = results.successful.length;
    const failedCount = results.failed.length;
    const feeWarnings = results.fee_warnings || [];

    if (successfulCount > 0) {
      const action =
        promotionData.value.promotionType === 'repeat'
          ? 'repeated'
          : promotionData.value.promotionType === 'transfer'
            ? 'transferred'
            : 'promoted'

      toast.success(
        t('promotion.success_count', {
          count: successfulCount,
          action: t(`promotion.${action}`)
        })
      );
    }

    if (failedCount > 0) {
      results.failed.forEach(failure => {
        const studentName = failure.student_name || `ID: ${failure.student_id}`
        toast.error(`${studentName}: ${failure.error}`, {
          timeout: 8000,
          closeOnClick: false
        })
      })

    }

    // Promotion itself can succeed while the new-year fee receipt quietly
    // fails to be created (e.g. the target class has no fee configured yet).
    // Surface that clearly instead of letting it pass unnoticed.
    if (feeWarnings.length > 0) {
      feeWarnings.forEach(warning => {
        toast.warning(`${warning.student_name}: ${warning.message}`, {
          timeout: 12000,
          closeOnClick: false
        })
      })
    }

    await loadStudents()
  } catch (error) {
    notifyError(error, 'errors.operation_failed')
  } finally {
    promoting.value = false
  }
}

const resetFilters = () => {
  filters.value = {
    sourceClassId: '',
    sourceClassGroupId: ''
  }
  promotionData.value = {
    targetAcademicYearId: academicYears.value.find(year => year.is_active)?.id || '',
    targetClassId: '',
    targetClassGroupId: '',
    promotionType: 'promote'
  }
  sourceGroups.value = []
  targetGroups.value = []
  students.value = []
  selectedStudents.value = []
  allSelected.value = false
}

watch(
  () => promotionData.value.targetClassId,
  newVal => {
    if (newVal) {
      loadTargetClassGroups()
    }
  }
)

onMounted(() => {
  loadInitialData()
})
</script>
